/**
 * The page the floating panel's chat is working for. The panel is mounted
 * app-wide, outside any workspace's providers, so a workspace that wants its
 * chat wired in (the client profile's Requests: the open intake rides each
 * turn, drafts Casey builds open in the center, Ask Casey prompts reach the
 * composer) registers a host here for its scope while it is mounted.
 */
import { create } from "zustand";

import type { AgentScope } from "./types";
import type { ChatAdapters } from "./useAgentChat";

export type HostPromptListener = (
  text: string,
  opts: { send: boolean }
) => void;

export type AgentChatHost = {
  scope: { kind: "client"; id: string };
  adapters: Partial<ChatAdapters>;
  /** Prompts the page pushes; `send` sends, otherwise the composer is seeded. */
  onPromptRequest: (listener: HostPromptListener) => () => void;
  /** A turn finished (or none is running). */
  onTurnIdle?: () => void;
};

type State = {
  host: AgentChatHost | null;
  register: (host: AgentChatHost) => () => void;
};

export const useAgentChatHostStore = create<State>()((set, get) => ({
  host: null,
  register: (host) => {
    set({ host });
    return () => {
      if (get().host === host) set({ host: null });
    };
  }
}));

/** The registered host when it works for `scope`, else null. */
export const useAgentChatHost = (scope: AgentScope): AgentChatHost | null =>
  useAgentChatHostStore((s) =>
    s.host && s.host.scope.kind === scope.kind && s.host.scope.id === scope.id
      ? s.host
      : null
  );
